import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import api from '../../services/api';
// 1. Import Turnstile
import { Turnstile } from '@marsidev/react-turnstile';

const Contact = () => {
    const { t, i18n } = useTranslation(); 
    const { user, isAuthenticated } = useAuth();
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: '',
        file: null,
        privacy_policy: false,
        newsletter: false
    });

    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState({ type: '', message: '' });
    
    // 2. Add state for the Cloudflare Token
    const [turnstileToken, setTurnstileToken] = useState(null);
    const turnstileRef = useRef();

    useEffect(() => {
        if (isAuthenticated && user?.email) {
            setFormData(prev => ({ ...prev, email: user.email }));
        }
    }, [isAuthenticated, user]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (status.type === 'error') setStatus({ type: '', message: '' });
    };

    const handleFileChange = (e) => {
        setFormData(prev => ({
            ...prev,
            file: e.target.files[0]
        }));
    };

    const validateForm = () => {
        if (!formData.name || !formData.email || !formData.subject || !formData.message) {
            setStatus({ type: 'error', message: t('contact.errors.all_fields_required') });
            return false;
        }
        if (!/\S+@\S+\.\S+/.test(formData.email)) {
            setStatus({ type: 'error', message: t('contact.errors.invalid_email') });
            return false;
        }
        if (!formData.privacy_policy) {
            setStatus({ type: 'error', message: t('contact.errors.privacy_required') });
            return false;
        }
        // 3. Ensure the Turnstile token exists before allowing submission
        if (!turnstileToken) {
            setStatus({ type: 'error', message: 'Please complete the security check.' }); // You can add a translation key for this later
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) return;

        setLoading(true);
        setStatus({ type: '', message: '' });

        const data = new FormData();
        data.append('name', formData.name);
        data.append('email', formData.email);
        data.append('subject', formData.subject);
        data.append('message', formData.message);
        data.append('privacy_policy', formData.privacy_policy);
        data.append('newsletter', formData.newsletter);
        data.append('language', i18n.language); 
        
        // 4. Append the Turnstile Token to your FormData
        data.append('turnstile_token', turnstileToken);

        if (formData.file) {
            data.append('file', formData.file);
        }

        try {
            await api.post('/users/contact/', data, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            
            setStatus({ 
                type: 'success', 
                message: t('contact.success.message_sent') 
            });
            
            setFormData(prev => ({
                name: '',
                email: isAuthenticated ? prev.email : '',
                subject: '',
                message: '',
                file: null,
                privacy_policy: false,
                newsletter: false
            }));

            // Reset Turnstile token so they can't send twice with the same token
            setTurnstileToken(null);
            if (turnstileRef.current) turnstileRef.current.reset();

        } catch (error) {
            setStatus({ 
                type: 'error', 
                message: error.response?.data?.error || t('contact.errors.generic_error') 
            });
            // If it failed, reset the captcha so they can try again
            setTurnstileToken(null);
            if (turnstileRef.current) turnstileRef.current.reset();
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex items-center justify-center min-h-screen bg-brand-dark text-white px-4 py-8">
            <div className="p-8 bg-brand-dark rounded-lg shadow-md w-full max-w-md border border-slate-700 hover:border-brand-orange">
                <h2 className="text-2xl font-bold text-center mb-6 text-white">{t('contact.title')}</h2>

                {status.message && (
                    <div className={`mb-4 p-3 rounded-md text-sm border ${
                        status.type === 'success' ? 'bg-green-900/50 border-green-600 text-green-200' : 'bg-red-900/50 border-red-600 text-red-200'
                    }`}>
                        {status.message}
                    </div>
                )}

                <form onSubmit={handleSubmit}>
                    {/* ... (Keep all your existing inputs exactly the same) ... */}
                    <div className="mb-4">
                        <label className="block text-orange-400 font-light mb-1">{t('contact.form.full_name')}</label>
                        <input type="text" name="name" value={formData.name} onChange={handleChange} className="w-full px-3 py-2 border rounded-md bg-slate-700 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500 text-white" />
                    </div>

                    <div className="mb-4">
                        <label className="block text-orange-400 font-light mb-1">{t('contact.form.email')}</label>
                        <input type="email" name="email" value={formData.email} onChange={handleChange} disabled={isAuthenticated} className={`w-full px-3 py-2 border rounded-md bg-slate-700 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500 text-white ${isAuthenticated ? 'opacity-75 cursor-not-allowed' : ''}`} />
                    </div>

                    <div className="mb-4">
                        <label className="block text-orange-400 font-light mb-1">{t('contact.form.subject')}</label>
                        <input type="text" name="subject" value={formData.subject} onChange={handleChange} className="w-full px-3 py-2 border rounded-md bg-slate-700 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500 text-white" />
                    </div>

                    <div className="mb-4">
                        <label className="block text-orange-400 font-light mb-1">{t('contact.form.message')}</label>
                        <textarea name="message" rows="4" value={formData.message} onChange={handleChange} className="w-full px-3 py-2 border rounded-md bg-slate-700 border-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500 text-white" />
                    </div>

                    <div className="mb-4">
                        <label className="block text-orange-400 font-light mb-1">{t('contact.form.upload_file')}</label>
                        <input type="file" onChange={handleFileChange} className="block w-full text-sm text-slate-300 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-slate-600 file:text-white hover:file:bg-slate-500" />
                    </div>

                    <div className="mb-6 space-y-3">
                        <div className="flex items-start">
                            <input type="checkbox" id="privacy_policy" name="privacy_policy" checked={formData.privacy_policy} onChange={handleChange} className="mr-2 h-4 w-4 mt-1 rounded bg-slate-700 border-slate-600 text-orange-500 focus:ring-orange-500" />
                            <label htmlFor="privacy_policy" className="text-sm text-gray-300">
                                {t('contact.form.privacy_agreement1')}
                                <Link to="/privacy" className="hover:underline text-orange-400">
                                    {t('contact.form.privacy_agreement2')}
                                </Link>
                                {t('contact.form.privacy_agreement3')}
                            </label>
                        </div>
                        <div className="flex items-start">
                            <input type="checkbox" id="newsletter" name="newsletter" checked={formData.newsletter} onChange={handleChange} className="mr-2 h-4 w-4 mt-1 rounded bg-slate-700 border-slate-600 text-orange-500 focus:ring-orange-500" />
                            <label htmlFor="newsletter" className="text-sm text-gray-300">{t('contact.form.newsletter_opt_in')}</label>
                        </div>
                    </div>

                    {/* 5. Cloudflare Turnstile Widget */}
                    <div className="mb-4 flex justify-center">
                        <Turnstile
                            siteKey="0x4AAAAAAEQ63GgLN7swPECf" // <-- Replace this with your Site Key
                            ref={turnstileRef}
                            onSuccess={(token) => setTurnstileToken(token)}
                            options={{ theme: 'dark' }} // Matches your dark UI perfectly
                        />
                    </div>

                    {/* Button disabled if loading OR if Turnstile isn't ready */}
                    <button 
                        type="submit" 
                        disabled={loading || !turnstileToken} 
                        className="w-full bg-brand-orange text-white py-2 rounded-md hover:bg-orange-600 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed border border-transparent hover:border-white" 
                        style={{ backgroundColor: 'rgb(0,35,35)', borderColor: 'rgb(40,55,55)' }}
                    >
                        {loading ? t('contact.form.sending') : t('contact.form.send')}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Contact;
